/**
 * useAnalytics — wrapper propre autour de Google Analytics 4 (gtag).
 *
 * Avantages :
 *  - 1 seul endroit pour modifier la logique de tracking (renommer un event, désactiver en dev, etc.)
 *  - Pas de "gtag is not defined" si le script GA n'a pas chargé
 *  - Logging dev pour vérifier visuellement les events sans ouvrir GA Real-time
 *
 * Usage :
 *   import { useAnalytics } from '@/composables/useAnalytics';
 *   const { trackEvent } = useAnalytics();
 *   trackEvent('itinerary_completed', { stops: 4 });
 *
 * Note : pas de trackPageView ici — l'app a une seule "page", et GA4 envoie
 * automatiquement le page_view à l'init.
 */

type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    gtag?: (...args: GtagArgs) => void;
    dataLayer?: unknown[];
  }
}

// True en dev (Vite injecte import.meta.env.DEV)
const isDev = import.meta.env.DEV;

export function useAnalytics() {
  /**
   * Envoie un événement custom à GA4.
   * Les noms d'events sont en snake_case (convention GA4).
   * Les paramètres sont optionnels et envoyés comme propriétés de l'event.
   */
  function trackEvent(eventName: string, params: Record<string, unknown> = {}): void {
    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[analytics]', eventName, params);
    }
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  }

  return {
    trackEvent,
  };
}

/**
 * Liste des events trackés dans EuroRail (référence centrale).
 * Ajoute un nouvel event ici quand tu en crées un, pour garder la doc à jour.
 */
export const ANALYTICS_EVENTS = {
  // Conversion principale (KPI)
  ITINERARY_STOP_ADDED: 'itinerary_stop_added',        // chaque ajout d'étape
  ITINERARY_COMPLETED: 'itinerary_completed',          // ≥ 2 étapes — KPI nord
  ITINERARY_SHARED: 'itinerary_shared',                // lien copié

  // Interactions carte
  MAP_FIRST_INTERACTION: 'map_first_interaction',      // 1er clic sur une ville
  MAP_MODE_CHANGED: 'map_mode_changed',                // train / bus / nuit
} as const;
